import { useState } from 'react'
import { Pressable, StyleSheet, Text, View } from 'react-native'
import { useResultViewed } from '../hooks/usePaywallTracking'
import { colors, fontFamily, fontSize, radius, shadow, space } from '../tokens'
import { BandCheckResultEvidence } from './BandCheckResultEvidence'
import { BandCheckResultHeader } from './BandCheckResultHeader'
import { BandCheckShareButton } from './BandCheckShareButton'
import { EmailCapture } from './EmailCapture'
import { PaidAppealCTA } from './PaidAppealCTA'
import { ScotlandViewOnlyBanner } from './ScotlandViewOnlyBanner'
import { SecondChancePrompt } from './SecondChancePrompt'

type EvidenceBlock = {
  id: string
  name: string
  suggestedBand: string | null
  reasoning?: readonly string[]
}

type Props = {
  result: {
    bandCheckId: string
    property: {
      addressLine1: string
      postcode: string
      localAuthority: string
      jurisdiction: 'ENGLAND' | 'WALES' | 'SCOTLAND'
    }
    currentBand: string
    suggestedBand: string
    confidence: number
    flaggedAsOverbanded: boolean
    estimatedAnnualSaving: number
    refund: {
      bestGuess: number
      rangeLow: number
      rangeHigh: number
      yearsApplied: number | null
    }
    matchedComparables?: readonly {
      address: string
      band: string
      lastSalePrice: number | null
      lastSaleDate: string | null
    }[]
    evidenceBlocks?: readonly EvidenceBlock[]
    totalScore?: number
    caseLabel?: 'Good case' | 'Strong case' | 'Excellent case' | null
    includedBlockIds?: readonly string[]
    subjectAttributesSource?: 'epc' | 'none'
    secondChance?: {
      eligible: boolean
      reason: string
      currentValueCapGbp: number | null
    }
  }
  onCheckAnother: () => void
}

export const BandCheckResult = ({ result, onCheckAnother }: Props) => {
  const includedSet = new Set(result.includedBlockIds ?? [])
  const includedBlocks = (result.evidenceBlocks ?? []).filter((b) => includedSet.has(b.id))
  const otherBlocks = (result.evidenceBlocks ?? []).filter((b) => !includedSet.has(b.id))
  const [secondChanceUnlocked, setSecondChanceUnlocked] = useState(false)
  // Beacon: a verdict rendered in the app (the marketing→app handoff landed).
  useResultViewed(result.bandCheckId, result.flaggedAsOverbanded, result.property.jurisdiction)
  const isScotland = result.property.jurisdiction === 'SCOTLAND'
  // Scotland is view-only: no per-property council tax data source, so no
  // £4.99 appeal pack and no second-chance AVM flow (PropertyData covers E&W
  // only). Suppress both prompts and surface a banner explaining the gap.
  const showSecondChance =
    !isScotland &&
    !result.flaggedAsOverbanded &&
    result.secondChance?.eligible === true &&
    result.secondChance.currentValueCapGbp != null
  const showPaidCta = !isScotland && (result.flaggedAsOverbanded || secondChanceUnlocked)
  const comparables = result.matchedComparables ?? []
  const teaserComparable =
    comparables.find((c) => c.lastSalePrice != null && c.lastSaleDate != null) ?? comparables[0]

  return (
    <View style={styles.outer}>
      <Pressable accessibilityRole="button" onPress={onCheckAnother} style={styles.back}>
        <Text style={styles.backText}>← Check another property</Text>
      </Pressable>

      <View style={styles.card}>
        <BandCheckResultHeader
          addressLine1={result.property.addressLine1}
          postcode={result.property.postcode}
          localAuthority={result.property.localAuthority}
          currentBand={result.currentBand}
          suggestedBand={result.suggestedBand}
          confidence={result.confidence}
          flaggedAsOverbanded={result.flaggedAsOverbanded}
          totalScore={result.totalScore}
          caseLabel={result.caseLabel}
          includedCount={includedBlocks.length}
          subjectAttributesSource={result.subjectAttributesSource}
          estimatedAnnualSaving={result.estimatedAnnualSaving}
          refund={result.refund}
        />

        <BandCheckResultEvidence
          includedBlocks={includedBlocks}
          otherBlocks={otherBlocks}
          teaserComparable={teaserComparable}
          totalComparableCount={comparables.length}
          showTeaser={showPaidCta}
          inlineReasoning={isScotland}
        />
      </View>

      {isScotland ? <ScotlandViewOnlyBanner /> : null}

      {showSecondChance && result.secondChance?.currentValueCapGbp != null ? (
        <SecondChancePrompt
          bandCheckId={result.bandCheckId}
          currentBand={result.currentBand}
          currentValueCapGbp={result.secondChance.currentValueCapGbp}
          onAgreed={() => setSecondChanceUnlocked(true)}
        />
      ) : null}

      {showPaidCta ? (
        <>
          <PaidAppealCTA bandCheckId={result.bandCheckId} />
          <EmailCapture bandCheckId={result.bandCheckId} />
        </>
      ) : null}

      <BandCheckShareButton bandCheckId={result.bandCheckId} />
    </View>
  )
}

const styles = StyleSheet.create({
  outer: { width: '100%', maxWidth: 560, gap: space[16] },
  back: {
    alignSelf: 'flex-start',
    paddingVertical: space[8],
  },
  backText: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.muted,
    fontFamily: fontFamily.body,
  },
  card: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: 32,
    ...shadow.card,
  },
})
